/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Suspense, lazy, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { Icon } from '@/components/Icon';
import { BootScreen } from '@/components/loading';
import { Button, StandalonePage } from '@/design-system';
import { useCurrentUser } from './useAccess';

const RequestAccessPage = lazy(() => import('./RequestAccessPage'));

/** Access was switched off deliberately, so there is nothing to ask for — only someone to ask. */
function AccessDisabled() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  return (
    <StandalonePage
      icon="do_not_disturb_on"
      iconTone="danger"
      title={t('accessDisabled.title')}
      description={t('accessDisabled.message')}
      actions={
        <Button type="button" variant="outline" onClick={() => signOut()}>
          <Icon name="logout" size={16} />
          {t('accessDisabled.signOut')}
        </Button>
      }
    />
  );
}

export function AccessGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCurrentUser();

  if (isLoading) {
    return <BootScreen status={t('signIn.checking')} />;
  }

  if (data?.status === 'DISABLED') {
    return <AccessDisabled />;
  }

  if (data?.status === 'ACTIVE') {
    return <>{children}</>;
  }

  return (
    <Suspense fallback={<BootScreen status={t('signIn.checking')} />}>
      <RequestAccessPage user={data} />
    </Suspense>
  );
}
